/**
 * Texto exato dos 2 modelos de Termo (Responsabilidade/Devolução), extraído literalmente
 * de `discovery/SI/Termos/*.docx` — uso verbatim (linguagem jurídica, não reescrita).
 * Únicas normalizações feitas: espaçamento/pontuação ao redor dos campos de mesclagem do
 * Word (`${...}`), que vinham colados por causa de como o docx divide o texto em "runs"
 * (ex. "CNPJ: 31.611.467/0001-00,vem" -> "..., vem") — o conteúdo em si não foi alterado.
 *
 * Funções puras (testadas em termoTemplates.test.ts) — quem monta o PDF de fato é
 * termoPdfService.ts, que só chama estas funções pra obter o texto já preenchido.
 */

export interface EmpresaProprietaria {
  nome: string;
  cnpj: string;
}

/** Dado fixo — a Raro é a proprietária padrão de todo equipamento, exceto quando o item
 * tem `propriedadeEquipamento === 'cliente'` (ver ConfigRegrasCliente.nomeEmpresaProprietaria/
 * cnpjEmpresaProprietaria, configurável por Cliente). */
export const EMPRESA_RARO: EmpresaProprietaria = {
  nome: 'RARO RECRUTAMENTO EM TI LTDA',
  cnpj: '31.611.467/0001-00',
};

/** Só o Termo de Responsabilidade tem esse parágrafo de abertura (o de Devolução não). */
export function montarParagrafoAbertura(empresa: EmpresaProprietaria): string {
  return (
    `A ${empresa.nome}, inscrita no CNPJ: ${empresa.cnpj}, vem por meio de este documento ` +
    'fornecer a título de recursos para o trabalho, sem direito à transferência de ' +
    'propriedade, os seguintes equipamentos:'
  );
}

export interface DadosDeclaracao {
  nome: string;
  funcao: string;
  cpf: string;
  /** Já formatada (ex. "15 de agosto de 2026") — ver montarLocaleData. */
  data: string;
}

export function montarDeclaracaoResponsabilidade({ nome, funcao, cpf, data }: DadosDeclaracao): string {
  return (
    `Através do presente termo, eu ${nome}, na função de ${funcao}, inscrito no CPF ${cpf}, ` +
    `declaro ter recebido, em ${data}, os equipamentos acima mencionados em perfeitas ` +
    'condições de uso, funcionamento, conservação e segurança.'
  );
}

export function montarDeclaracaoDevolucao({ nome, funcao, cpf, data }: DadosDeclaracao): string {
  return (
    `Através do presente termo, eu ${nome}, na função de ${funcao}, inscrito no CPF ${cpf}, ` +
    // "acima" (não "abaixo" como no modelo .docx): no termo gerado a tabela de
    // equipamentos vem antes desta declaração.
    `declaro ter DEVOLVIDO, em ${data}, os equipamentos acima mencionados.`
  );
}

/** "Cidade, DD de mês por extenso de AAAA" — formato confirmado com o usuário. */
export function montarLocaleData(cidade: string, data: Date): string {
  const meses = [
    'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
    'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro',
  ];
  return `${cidade}, ${data.getDate()} de ${meses[data.getMonth()]} de ${data.getFullYear()}`;
}

/** Precede a lista de regras/condições, no Termo de Responsabilidade. */
export const TEXTO_INTRODUCAO_CLAUSULAS =
  'Declaro, outrossim, haver sido comunicado pela referida empresa que a utilização do ' +
  'bem em questão encontra-se vinculada a regras e condições próprias, a saber:';

/** As 7 cláusulas fixas do Termo de Responsabilidade, na ordem do modelo original. */
export const CLAUSULAS_RESPONSABILIDADE: string[] = [
  'Sua finalidade é estritamente profissional, isto é, para viabilizar a prestação de ' +
    'serviços para as quais fui contratado. Em consequência disso, estou ciente de que é ' +
    'proibido o download de arquivos particulares bem como o acesso a sites indevidos no ' +
    'uso da internet, mesmo que o equipamento esteja conectado em outras redes, conforme ' +
    'as políticas da organização.',
  'Não é permitido o empréstimo, troca ou substituição dos equipamentos entre ' +
    'colaboradores sem o prévio e expressa autorização do time de Segurança da Informação ' +
    'da Raro Labs. Na ocasião de substituições, um novo termo deverá ser assinado pelo ' +
    'colaborador.',
  'Estou ciente de que o time de Segurança da Informação realizará auditorias periódicas ' +
    'a fim de verificar o cumprimento das normas constantes da Política de Segurança da ' +
    'Informação.',
  'Não é permitido levar o equipamento em viagem internacional. Caso seja necessária a ' +
    'viagem para o exterior levando o equipamento, comunique previamente e formalmente à ' +
    'equipe de Segurança da Informação da Raro Labs. Vou arcar com todos os custos ' +
    'referentes ao transporte do equipamento, bem como a devolução, se for o caso.',
  'Devo zelar pelo equipamento e arcar com os custos em caso de extravio ou quaisquer ' +
    'danos que o equipamento venha sofrer sob minha guarda conforme valor de equipamento ' +
    'da mesma marca ou equivalente a ser informado pelo Departamento de RH.',
  'Devo garantir a devolução do(s) equipamento(s) e seus acessórios no prazo de 07 (sete) ' +
    'dias a partir do último dia de trabalho, em caso de rescisão contratual, sob pena de ' +
    'desconto em eventual valor rescisório a ser pago, bem como execução pela empresa dos ' +
    'valores cujo saldo rescisório não for suficiente para satisfazê-lo;',
  'Nos termos do Art. 462, parágrafo 1º, da CLT, desde já autorizo e concordo com o ' +
    'desconto em folha de pagamento e/ou rescisão contratual na ocorrência das hipóteses ' +
    'previstas nos itens acima deste Termo.',
];

/** Última linha do Termo de Devolução, depois da tabela de equipamentos. */
export const TEXTO_ATESTADO_DEVOLUCAO =
  'Atestamos para os devidos fins que, foram devolvidos neste ato os equipamentos ' +
  'descritos acima e seus acessórios relacionados neste termo.';

/** Cláusula LGPD — adicionada por pedido do usuário, nos 2 termos. */
export const CLAUSULA_LGPD =
  'Declaro ciência de que a coleta dos dados pessoais neste termo se dá exclusivamente ' +
  'para fins de execução deste documento, nos termos do Art. 7º, inciso V, da Lei nº ' +
  '13.709/2018 (Lei Geral de Proteção de Dados).';

export const LABEL_COLABORADOR = 'COLABORADOR(A)';
export const LABEL_TESTEMUNHA = 'TESTEMUNHA';
/** Quem gera o Termo de Devolução (o modelo .docx original dizia "TÉCNICO"). */
export const LABEL_SEGURANCA_INFORMACAO = 'TIME DE SEGURANÇA DA INFORMAÇÃO';
